// The feature registry behind the navbar "Features" dropdown and the /features page.
//
// Deliberately dependency-free, exactly like segments/registry.js: the ordering, the anchor
// hrefs and the route verdict are the parts with real rules, so they live here as pure data and
// pure functions and are unit-tested in registry.test.js (node --test). The nav, the sidebar and
// the page all read from this one list, which is what keeps them from drifting apart.
//
// TRACE: every `capability` below names the spec in openspec/specs/ (or the change that shipped
// it) backing the claim — the same "no claim without a shipped capability" rule the segment copy
// follows. If a capability is ever removed, its bullet comes out of the copy with it.

export const FEATURES_PATH = '/features'

// Which platform brands (brand.brand_key) show a brand-specific feature, as for segments in
// segments/registry.js: joulelingo.com is the schools product, kilosight.com the one for
// businesses, landlords and homes, and 'power' (the default host) shows everything.
const SCHOOLS_BRANDS = ['power', 'joulelingo']
const BUSINESS_BRANDS = ['power', 'kilosight']
const FEATURE_BRANDS = new Set([...SCHOOLS_BRANDS, ...BUSINESS_BRANDS])
const LEAD_FEATURE = { joulelingo: 'learning' }

// Order drives both the dropdown and the page. Data first, then what we do with it, then what
// comes back out — a visitor reading top to bottom gets the pipeline in the order it runs.
export const FEATURES = [
    {
        key: 'data',
        // Dropdown copy. Short label, one line of why — long enough to distinguish two
        // neighbouring items, short enough to stay on one line in the menu.
        navTitle: 'Half-hourly data',
        navBlurb: 'Both fuels, straight from your suppliers',
        title: 'Electricity and gas, half-hourly',
        lede: 'Readings pulled from the suppliers and meters you already have, into one place: both fuels together, with the energy mix, combined cost and carbon on a single view.',
        points: [
            {
                title: 'Both fuels, one timeline',
                detail: 'Electricity and gas aggregated to the same half-hourly grid, so the mix, the totals and the daily shape are directly comparable rather than living in two portals.',
                capability: 'consumption-aggregation'
            },
            {
                title: 'Straight from your providers',
                detail: 'We connect to your existing utility providers and smart meters and keep pulling: no new hardware, and no change to how your buildings are run.',
                capability: 'meter-provider-assignment / fetch-scale-hardening'
            },
            {
                title: 'CSV when there is no connector',
                detail: 'If a provider is not supported yet, half-hourly data can be imported from CSV or spreadsheet and lands in exactly the same place.',
                capability: 'reading-import / reading-import-ui'
            },
            {
                title: 'The raw readings stay yours',
                detail: 'Explore the underlying half-hourly series and export it, or pull it through the API into whatever you already report from.',
                capability: 'power-explore-export / power-export-api'
            }
        ]
    },
    {
        key: 'cost',
        navTitle: 'Cost & tariffs',
        navBlurb: 'Tariff-accurate cost, checked against bills',
        title: 'Cost you can trust',
        lede: 'A tariff engine that handles the awkward parts (standing charges, time-of-use bands, Economy 7, Agile), then checks the supplier bill, line by line, against what the meters actually recorded and the rate you are contracted to pay.',
        points: [
            {
                title: 'Tariffs modelled properly',
                detail: 'Standing charges, unit rates, time-of-use bands, Economy 7 and half-hourly Agile pricing, applied to the readings rather than averaged over the month.',
                capability: 'tariff-model / cost-calculation'
            },
            {
                title: 'Bills checked against the meter',
                detail: 'Each invoice reconciled against the recorded consumption, so an overcharge or a misread shows up as a discrepancy instead of quietly being paid.',
                capability: 'bill-validation'
            },
            {
                title: 'Statements checked line by line',
                detail: 'Each charge on a statement (unit rates, standing charge, CCL, VAT) checked against both the metered consumption and the rate you are contracted to pay, so a discrepancy names the line that is wrong rather than just the total.',
                capability: 'bill-statement-model / bill-discrepancy-alert'
            },
            {
                title: 'Every contract on the record',
                detail: 'End dates, notice periods and rates recorded per supply, so nobody has to go looking through a filing cabinet to answer what a site is committed to.',
                capability: 'supply-contract-registry'
            },
            {
                title: 'Cost per site, not just per bill',
                detail: 'Spend attributed down to the individual property and meter, with occupancy taken into account, so the expensive buildings are identifiable.',
                capability: 'estate-per-location-cost-occupancy'
            }
        ]
    },
    {
        key: 'targets',
        navTitle: 'Targets & budgets',
        navBlurb: 'Reduction targets and annual budgets',
        title: 'Targets and budgets',
        lede: 'Set a per-fuel reduction target and an annual budget, then track progress against a baseline with a seasonally-projected year-end position, so "use less energy" becomes a number everyone can see.',
        points: [
            {
                title: 'A target per fuel',
                detail: 'Set a reduction target against a baseline period and track progress towards it, per fuel, rather than arguing about one blended percentage.',
                capability: 'target-model / target-progress / target-ui'
            },
            {
                title: 'Budgets that project forward',
                detail: 'An annual budget tracked through the year with a projected year-end position, so an overrun is visible while there is still time to act on it.',
                capability: 'budget-model / budget-tracking-ui'
            },
            {
                title: 'Weather taken out of the picture',
                detail: 'Degree-day normalisation means a cold quarter is not mistaken for a failed intervention, and a mild one is not mistaken for a saving.',
                capability:
                    'degree-day-computation / weather-normalised-insights'
            },
            {
                title: 'Try a change before committing',
                detail: 'What-if sliders put a number on an everyday change (switching off at lunchtime, cutting standby) in kWh, cost-free miles of electric driving and CO₂, before anyone commits to it.',
                capability: 'power-what-if-sliders / consumption-forecast'
            }
        ]
    },
    {
        key: 'alerts',
        navTitle: 'Alerts & waste',
        navBlurb: 'Baseload creep, overruns and renewals',
        title: 'Alerts worth reading',
        lede: 'Creeping baseload, projected budget overrun, unusual demand and a contract renewal deadline or lapsed supply: routed to the people who can act, plus weekly or monthly digest emails.',
        points: [
            {
                title: 'Baseload that creeps up',
                detail: 'The overnight floor is watched for drift, which is where a left-on plant item or a failed schedule shows up first. It is usually the cheapest saving available.',
                capability: 'baseload-creep-alert / out-of-hours-insights'
            },
            {
                title: 'The holidays you are paying for',
                detail: 'Every closure period costed in £ and CO₂, by site or by meter, so empty-building waste stops being invisible.',
                capability: 'holiday-analytics / holiday-analysis-ui'
            },
            {
                title: 'Warning before the bill',
                detail: 'A projected budget overrun raises an alert while the year is still running, not when the invoice lands.',
                capability: 'budget-breach-alert'
            },
            {
                title: 'Renewal deadlines that do not slip',
                detail: 'An alert before a contract end date or notice deadline, so nobody rolls onto expensive deemed rates by accident.',
                capability: 'supply-contract-registry / contract-renewal-alerts'
            },
            {
                title: 'Routed to a person, not a mailbox',
                detail: 'Rules decide who hears about what, and weekly or monthly digests keep the people who do not need an interruption in the loop anyway.',
                capability:
                    'alerts-rules / alerts-workflow / report-subscriptions / digest-generation'
            }
        ]
    },
    {
        key: 'carbon',
        navTitle: 'Carbon reporting',
        navBlurb: 'SECR-shaped reports on official factors',
        title: 'Carbon reporting that stands up',
        lede: 'SECR-shaped annual reports built on maintained government factor tables, exportable as PDF or CSV: ready for boards, owners and funders without rebuilding a spreadsheet.',
        points: [
            {
                title: 'Annual reports, generated',
                detail: 'An SECR-shaped report assembled from the same half-hourly data the rest of the platform runs on, rather than a separate annual exercise.',
                capability: 'carbon-report-generation'
            },
            {
                title: 'Factors kept current',
                detail: 'Maintained government emission factor tables, versioned by year, so last year’s report still reconciles with the factors it was built on.',
                capability: 'carbon-factor-tables'
            },
            {
                title: 'Exportable as it stands',
                detail: 'PDF for the board pack, CSV for whoever wants to check the arithmetic.',
                capability: 'carbon-report-export'
            },
            {
                title: 'Numbers people can picture',
                detail: 'Equivalents, including woodland area, turn a tonnage into something an audience without an energy background can actually hold on to.',
                capability: 'power-equivalents / power-woodland-equivalent'
            }
        ]
    },
    {
        key: 'portfolio',
        navTitle: 'Portfolio comparison',
        navBlurb: 'Fair league tables across every building',
        title: 'Compare every building',
        lede: 'A rollup across the organisations in a group or partner portfolio, with league tables normalised by each organisation’s floor area and headcount, so organisations of different sizes can be compared fairly and the outliers are obvious.',
        points: [
            {
                title: 'One view across the estate',
                detail: 'Totals and per-property breakdown across every building you are responsible for, with each site keeping its own account and its own data underneath.',
                capability: 'portfolio-dashboard / estate-property-portfolio'
            },
            {
                title: 'League tables that are fair',
                detail: 'Across organisations, rankings are normalised by each organisation’s floor area and headcount, so a large organisation and a small one can sit in the same table. Within one estate or business, the property league ranks properties by consumption, cost or out-of-hours share with movement against the previous period, and by kWh per m² and per occupant once each property’s floor area and occupancy are recorded.',
                capability:
                    'portfolio-rankings-ui / location-profile-fields / estate-property-portfolio'
            },
            {
                title: 'Compare against your peers',
                detail: 'Opt-in anonymised benchmarking against a matched cohort, alongside published "vs typical" reference figures for when a cohort is not available.',
                capability: 'anonymised-peer-benchmarking / static-benchmarks'
            },
            {
                title: 'Reported in your own language',
                detail: 'Terminology follows the kind of estate you run, whether you call them houses, halls, properties or sites, so the dashboards read as if they were built for you.',
                capability: 'sector-terminology / org-flavour'
            }
        ]
    },
    {
        key: 'learning',
        // The schools brand only (see `brands`): Kilosight's visitors never see a school word.
        brands: SCHOOLS_BRANDS,
        navTitle: 'Learning & play',
        navBlurb: 'A weekly quiz, activities and real datasets',
        title: 'Energy pupils can play with and learn from',
        lede: 'A weekly quiz, classroom activities, real datasets, a house competition and a team goal, all built from your own school’s meters, so pupils learn about energy from the buildings they sit in.',
        points: [
            {
                title: 'A weekly quiz on your own buildings',
                detail: 'Ten questions a week from your own meter data, pitched at ages 7–11, 11–14 or 14–16, with a leaderboard by pupil and by house, a question of the day and practice rounds. Teachers get a class mode for the board, a printable worksheet and a view of which questions the class got wrong.',
                capability: 'energy-quiz / engagement-feedback'
            },
            {
                title: 'Activities for every key stage',
                detail: 'A library of pupil activities and staff actions, filtered by key stage and subject, with each one recorded against the class and house that did it.',
                capability:
                    'education-content-model / education-library-ui / education-recording'
            },
            {
                title: 'Real datasets for lessons',
                detail: 'Ready-made packs from your own meters with starter questions (term against holidays, summer against winter, a day in thirty-minute steps), or build your own, as CSV or Excel.',
                capability: 'power-class-projects-ui / power-export-api'
            },
            {
                title: 'A house competition that is fair',
                detail: 'Houses earn points for quiz scores, turnout and activities, and houses with their own meters are ranked on how much they cut rather than how big they are.',
                capability: 'house-standings / pupil-groups'
            },
            {
                title: 'Energy as a trip on a map',
                detail: 'Yesterday’s electricity shown as how far an electric car could drive from your school, alongside kettles, phones and woodland, so the numbers mean something to a nine-year-old.',
                capability: 'ev-trip-visualization / power-equivalents'
            },
            {
                title: 'A team goal, streaks and badges',
                detail: 'Jolt sets one goal for the whole school each week, gives a tip of the day from your own readings, and keeps each pupil’s streak and badges. Teachers see class totals only.',
                capability: 'pupil-home'
            },
            {
                title: 'Your week on the hallway TV',
                detail: 'The week’s energy, the team goal and the house league on any TV, for the whole school or building by building, opened with a five-character code so nobody stays signed in on it.',
                capability: 'hallway-screen / house-standings'
            }
        ]
    },
    {
        key: 'access',
        // The business and property brand only: tenants and site managers are its audience.
        brands: BUSINESS_BRANDS,
        navTitle: 'Sites, tenants & teams',
        navBlurb: 'Logins per site, recharges and categories',
        title: 'Every site, tenant and team in its place',
        lede: 'Give each site manager or tenant a login to just their own buildings, recharge tenants from the meter, and break usage down by what it powers.',
        points: [
            {
                title: 'A login for each site manager or tenant',
                detail: 'Read-only access to their own sites or property: usage, alerts, closures and that site’s cost, plus their own email summary, without seeing the rest of the estate.',
                capability:
                    'location-scoped-access / report-subscriptions / alerts-notifications'
            },
            {
                title: 'Tenant recharge statements',
                detail: 'Monthly statements built from each tenant’s consumption and your tariff, with communal meters shared out, VAT and an optional admin fee, emailed as a PDF.',
                capability: 'tenant-recharge'
            },
            {
                title: 'A change of tenancy in one sitting',
                detail: 'Closing and opening readings from the half-hourly data, typed in or from a photo of the meter, with the cost split at the changeover.',
                capability: 'change-of-tenancy / tenancy-model'
            },
            {
                title: 'Usage by what it powers',
                detail: 'Tag meters as kitchen, refrigeration, cellar cooling, plant and HVAC, lighting, EV charging or generation, and see each site’s usage by category, with alerts on just one.',
                capability: 'meter-categories / alerts-rules'
            },
            {
                title: 'Opening hours as they really are',
                detail: 'Split shifts, trading past midnight, summer and winter hours, and bank holidays for each part of the UK, so out-of-hours means your hours.',
                capability: 'occupancy-schedule / sector-neutral-calendar'
            }
        ]
    }
]

/**
 * Features to show on this brand's site, in order. A feature without `brands` shows on every
 * brand; an unknown or missing key reads as the default brand, which shows all. A brand's
 * lead feature (Joulelingo: learning) comes first, so the schools site opens with it.
 */
export function getVisibleFeatures(brandKey) {
    const key = FEATURE_BRANDS.has(brandKey) ? brandKey : 'power'
    const visible = FEATURES.filter(
        (feature) => !feature.brands || feature.brands.includes(key)
    )
    const lead = LEAD_FEATURE[key]
    return lead
        ? [
              ...visible.filter((feature) => feature.key === lead),
              ...visible.filter((feature) => feature.key !== lead)
          ]
        : visible
}

/** Href for a feature's section on the features page. */
export function featureHref(key) {
    return `${FEATURES_PATH}#${key}`
}

/** The feature with this key, or undefined. */
export function getFeature(key) {
    return FEATURES.find((feature) => feature.key === key)
}

/**
 * What a /features request should do.
 *
 * Returns `{ action: 'render' }` or `{ action: 'redirect', to: '/' }`. As with the segment
 * spokes, every failure mode lands on the homepage rather than a 404 — these URLs go in emails,
 * ads and decks, and a stale one should still show the visitor something useful.
 *
 * Note there is no partner-host gate: capabilities are the product, and a partner reselling it
 * benefits from a features page carrying their brand. Only platform *pricing* is host-gated, and
 * that decision stays in segments/registry.js where the pricing rule already lives.
 *
 * @param {object} options
 * @param {boolean} options.isAuthenticated a signed-in user followed a marketing link
 * @param {boolean} options.isBeta          public routes disabled by beta mode
 */
export function resolveFeaturesRoute({ isAuthenticated, isBeta }) {
    if (isAuthenticated || isBeta) return { action: 'redirect', to: '/' }
    return { action: 'render' }
}
